import { useRef, useEffect, useCallback } from 'react';

/**
 * ArborVisualization — React Canvas 生命树组件
 * 从 Mockup 的 ArborVisualization.js + arbor-grid 迁移而来
 * 与 Mockup 原版参数完全一致
 */

interface ArborProps {
  /** 生命等级 0–100，控制树的生长程度 */
  level?: number;
  /** 自定义容器样式 */
  style?: React.CSSProperties;
  /** 自定义容器 className */
  className?: string;
}

/* ── 颜色常量（与 Mockup 一致） ── */
const COLORS = {
  shadow: '#0F0F0F',
  trunkDead: '#A39E93',
  leaf: ['#313D29', '#4A5D3F', '#7A8B6B'],
  energy: ['#E65A28', '#C44519', '#F07A4E'],
};

/* ── 数据结构 ── */
interface Stem {
  x1: number; y1: number;
  x2: number; y2: number;
  depth: number; normDepth: number;
  angle: number; isBroken: boolean;
}

interface Leaf {
  baseX: number; baseY: number;
  x: number; y: number;
  rngVal: number;
  radius: number;
  offsetA: number; offsetB: number;
  speed: number; driftRadius: number;
}

interface Flower {
  x: number; y: number;
  rngVal: number;
  stemDepth: number;
  targetRadius: number;
}

/* ── 辅助函数 ── */
function createLeaf(x: number, y: number): Leaf {
  return {
    baseX: x, baseY: y, x, y,
    rngVal: Math.random(),
    radius: Math.random() * 1.5 + 1.0,  // 与 grid 版一致
    offsetA: Math.random() * 100,
    offsetB: Math.random() * 100,
    speed: Math.random() * 0.001 + 0.0005,
    driftRadius: Math.random() * 4 + 1,  // 与 grid 版一致：4+1
  };
}

function createFlower(x: number, y: number, normDepth: number): Flower {
  return {
    x, y,
    rngVal: Math.random(),
    stemDepth: normDepth,
    targetRadius: Math.random() * 1.5 + 2.5,  // 与 grid 版一致
  };
}

/* ── 树结构生成（与 grid 版 classic 样式一致的参数） ── */
function generateTree(w: number, h: number) {
  const stems: Stem[] = [];
  const leaves: Leaf[] = [];
  const flowers: Flower[] = [];
  const startX = w / 2;
  const startY = h * 0.85;  // 下移树根，给树冠留出足够空间
  const initialLength = h * 0.14;  // 适当缩短初始长度，避免树冠超出顶部
  const maxDepth = 9;

  const branch = (x: number, y: number, len: number, angle: number, depth: number) => {
    const endX = x + Math.cos(angle) * len;
    const endY = y + Math.sin(angle) * len;
    const normDepth = depth / maxDepth;

    stems.push({
      x1: x, y1: y, x2: endX, y2: endY,
      depth, normDepth, angle,
      isBroken: Math.random() > 0.8 && normDepth < 0.6,
    });

    // 与 grid 版完全一致的叶子/花朵生成
    if (depth <= 10) {
      const spawnChance = Math.max(0.4, 1.0 - depth * 0.06);
      const isTip = depth === 0;
      if (Math.random() < spawnChance) {
        if (Math.random() < 0.8) {
          // 叶子 — 使用 grid 版的更高密度参数
          const leafCount = isTip ? 6 : Math.floor(15 + depth * 8);
          const scatter = depth === 0 ? 0 : depth * 9 + 5;
          for (let i = 0; i < leafCount; i++) {
            const r = scatter * Math.sqrt(Math.random());
            const theta = Math.random() * 2 * Math.PI;
            leaves.push(createLeaf(endX + r * Math.cos(theta), endY + r * Math.sin(theta)));
          }
        } else {
          // 花朵 — 限制在外层 depth <= 3
          if (depth <= 3) {
            for (let f = 0; f < 1; f++) {
              const s = depth * 10;
              flowers.push(createFlower(
                endX + (Math.random() - 0.5) * s,
                endY + (Math.random() - 0.5) * s,
                normDepth,
              ));
            }
          }
        }
      }
    }
    if (depth === 0) return;

    // classic 样式的分支
    let numBranches = 2;
    if (Math.random() > 0.85) numBranches = 3;
    for (let i = 0; i < numBranches; i++) {
      const spread = 0.45;  // grid 版 classic: 0.45
      const newAngle = angle + (i === 0 ? -spread : spread) + (Math.random() - 0.5) * 0.2;
      branch(endX, endY, len * (0.78 + Math.random() * 0.05), newAngle, depth - 1);
    }
  };

  branch(startX, startY, initialLength, -Math.PI / 2, maxDepth);
  return { stems, leaves, flowers };
}

/* ── 噪声 / 风摇 ── */
function getNoise(px: number, py: number, rootY: number, h: number, globalTime: number, t: number) {
  const distY = Math.max(0, rootY - py) / (h * 0.75);
  const swayFactor = Math.pow(distY, 2.0);
  const baseAmp = (1 - t) * 12 + t * 4.0;
  const amp = baseAmp * swayFactor;  // 与 grid 版一致：无 *0.5
  const phase = globalTime * 0.0015 - distY * 1.5 + px * 0.001;
  return { x: Math.sin(phase) * amp * 3.5, y: Math.cos(phase * 1.5) * amp * 0.5 };
}

/* ── Grid 版颜色函数（直接用 growthLevel t） ── */
function getLeafColor(t: number, rngVal: number): string {
  let gIdx = Math.floor(rngVal * COLORS.leaf.length);
  if (gIdx >= COLORS.leaf.length) gIdx = COLORS.leaf.length - 1;
  const target = COLORS.leaf[gIdx];
  const r2 = parseInt(target.substring(1, 3), 16);
  const g2 = parseInt(target.substring(3, 5), 16);
  const b2 = parseInt(target.substring(5, 7), 16);
  const r1 = parseInt(COLORS.trunkDead.substring(1, 3), 16);
  const g1 = parseInt(COLORS.trunkDead.substring(3, 5), 16);
  const b1 = parseInt(COLORS.trunkDead.substring(5, 7), 16);

  let colorT = t < 0.3 ? 0 : (t - 0.3) / 0.7;
  colorT = Math.pow(colorT, 1.5);
  let alpha = 0.1 + 0.9 * t;
  if (t <= 0.05) alpha = 0;

  const r = Math.round(r1 + (r2 - r1) * colorT);
  const g = Math.round(g1 + (g2 - g1) * colorT);
  const b = Math.round(b1 + (b2 - b1) * colorT);
  return `rgba(${r},${g},${b},${alpha})`;
}

function getFlowerColor(t: number, rngVal: number): string {
  let eIdx = Math.floor(rngVal * COLORS.energy.length);
  if (eIdx >= COLORS.energy.length) eIdx = COLORS.energy.length - 1;
  const target = COLORS.energy[eIdx];
  const r2 = parseInt(target.substring(1, 3), 16);
  const g2 = parseInt(target.substring(3, 5), 16);
  const b2 = parseInt(target.substring(5, 7), 16);
  const r1 = parseInt(COLORS.trunkDead.substring(1, 3), 16);
  const g1 = parseInt(COLORS.trunkDead.substring(3, 5), 16);
  const b1 = parseInt(COLORS.trunkDead.substring(5, 7), 16);

  let colorT = t < 0.6 ? 0 : (t - 0.6) / 0.4;
  colorT = Math.pow(colorT, 2);
  let alpha = 0.1 + 0.9 * t;
  if (t <= 0.05) alpha = 0;

  const r = Math.round(r1 + (r2 - r1) * colorT);
  const g = Math.round(g1 + (g2 - g1) * colorT);
  const b = Math.round(b1 + (b2 - b1) * colorT);
  return `rgba(${r},${g},${b},${Math.min(1.0, alpha)})`;
}

/* ── React 组件 ── */
export function ArborVisualization({ level = 100, style, className }: ArborProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<{
    stems: Stem[]; leaves: Leaf[]; flowers: Flower[];
    time: number; lastTime: number; animId: number | null; running: boolean;
    w: number; h: number;
  }>({ stems: [], leaves: [], flowers: [], time: 0, lastTime: 0, animId: null, running: false, w: 0, h: 0 });

  /* 重新布局 + 重新生成 */
  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const w = container.clientWidth;
    const h = container.clientHeight;
    const dpr = window.devicePixelRatio || 2;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(dpr, dpr);
    const tree = generateTree(w, h);
    const s = stateRef.current;
    s.stems = tree.stems;
    s.leaves = tree.leaves;
    s.flowers = tree.flowers;
    s.w = w;
    s.h = h;
  }, []);

  /* 渲染帧 — 使用 grid 版的渲染逻辑 */
  const animate = useCallback((currentTime: number) => {
    const s = stateRef.current;
    if (!s.running) return;
    s.animId = requestAnimationFrame(animate);
    if (!s.lastTime) s.lastTime = currentTime;
    s.lastTime = currentTime;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rawT = (level - 1) / 99;
    const t = rawT * rawT * (3 - 2 * rawT);
    const timeScale = 0.15 + 0.85 * rawT * rawT;
    s.time += 16 * timeScale;

    // 透明背景 — 使 mix-blend-mode 生效
    ctx.clearRect(0, 0, s.w, s.h);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const rootY = s.stems.length > 0 ? s.stems[0].y1 : s.h / 2;

    /* 树干 */
    s.stems.forEach(stem => {
      const requiredDepth = 0.25 * (1 - t);
      if (stem.normDepth < requiredDepth || (t < 0.6 && stem.isBroken && stem.normDepth < requiredDepth + 0.15)) return;
      ctx.beginPath();
      let fx2 = stem.x2, fy2 = stem.y2;
      if (stem.isBroken && t < 0.5) {
        const bf = 1 - t * 2;
        fx2 = stem.x1 + (stem.x2 - stem.x1) * 0.6 + Math.sin(s.time * 0.01) * 10 * bf;
        fy2 = stem.y1 + (stem.y2 - stem.y1) * 0.6 + Math.cos(s.time * 0.01) * 10 * bf;
        ctx.strokeStyle = `rgba(184,122,98,${0.4 + 0.4 * bf})`;
      } else {
        const r = Math.floor(122 * (1 - t) + 44 * t);
        const g = Math.floor(117 * (1 - t) + 42 * t);
        const b = Math.floor(113 * (1 - t) + 41 * t);
        ctx.strokeStyle = `rgba(${r},${g},${b},${0.15 + t * 0.3})`;
      }
      const n1 = getNoise(stem.x1, stem.y1, rootY, s.h, s.time, t);
      const n2 = getNoise(fx2, fy2, rootY, s.h, s.time, t);
      ctx.lineWidth = Math.max(0.5, stem.depth * 0.5 * (0.5 + t * 0.5));
      ctx.moveTo(stem.x1 + n1.x, stem.y1 + n1.y);
      ctx.lineTo(fx2 + n2.x, fy2 + n2.y);
      ctx.stroke();
    });

    /* 叶子 — 使用 grid 版渲染逻辑 */
    s.leaves.forEach(leaf => {
      if (t < 0.1) return;
      const localScale = Math.max(0, Math.min(1, (t - 0.1) / 0.7));
      if (localScale <= 0) return;

      leaf.x = leaf.baseX + Math.sin(s.time * leaf.speed + leaf.offsetA) * leaf.driftRadius;
      leaf.y = leaf.baseY + Math.cos(s.time * leaf.speed * 1.2 + leaf.offsetB) * leaf.driftRadius;
      const n = getNoise(leaf.x, leaf.y, rootY, s.h, s.time, t);

      ctx.beginPath();
      ctx.arc(leaf.x + n.x, leaf.y + n.y, leaf.radius * localScale, 0, Math.PI * 2);
      ctx.fillStyle = getLeafColor(t, leaf.rngVal);
      ctx.globalAlpha = localScale;
      ctx.fill();
      ctx.globalAlpha = 1.0;
    });

    /* 花朵 — 使用 grid 版渲染逻辑 */
    s.flowers.forEach(flower => {
      const growthLevel = flower.stemDepth + (1 - flower.stemDepth) * (flower.rngVal * 0.5);
      if (t <= 0.05 || t < growthLevel || growthLevel < 0.2) return;
      const localScale = Math.max(0, Math.min(1, (t - growthLevel) / 0.6));
      if (localScale <= 0) return;

      const n = getNoise(flower.x, flower.y, rootY, s.h, s.time, t);
      ctx.beginPath();
      ctx.arc(flower.x + n.x, flower.y + n.y, flower.targetRadius * localScale, 0, Math.PI * 2);
      ctx.fillStyle = getFlowerColor(t, flower.rngVal);
      ctx.globalAlpha = localScale * 0.95;
      ctx.fill();
      ctx.globalAlpha = 1.0;
    });
  }, [level]);

  /* 初始化 & 销毁 */
  useEffect(() => {
    resize();
    const s = stateRef.current;
    s.running = true;
    s.animId = requestAnimationFrame(animate);

    const onResize = () => resize();
    window.addEventListener('resize', onResize);
    return () => {
      s.running = false;
      if (s.animId) cancelAnimationFrame(s.animId);
      window.removeEventListener('resize', onResize);
    };
  }, [resize, animate]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ position: 'relative', width: '100%', ...style }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          background: 'transparent',
          mixBlendMode: 'multiply',  // 与 Mockup 一致
        }}
      />
    </div>
  );
}
